import React, { useEffect, useState } from 'react';

import { Divider, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import OrderCancelledModal from 'components/order-cancelled-modal';
import { useNavigation } from 'hooks/navigation/use-navigation';
import useDialogModal from 'hooks/use-dialog-modal';
import useErrorModal from 'hooks/use-error-modal';
import { useEtaCounter } from 'hooks/use-eta-counter/';
import usePolling from 'hooks/use-polling';
import fetchDeliveryStatus from 'remote/api/delivery-status';
import { useDeliveryConfirmationContext } from 'state/delivery-confirmation';
import { DeliveryStatus } from 'state/order/types';
import { routes } from 'utils/routing';

import CourierDetail from './courier-detail';
import DeliveryInfo from './delivery-order-card/delivery-info';
import DeliveryOrderDetails from './delivery-order-card/delivery-order-details';
import DeliveryProgress from './delivery-progress';
import { CardHeading, CardHeadingContainer, DeliveryCardContainer } from './styled';

interface IDeliveryDetailsProps {
  serverOrder: IServerOrder;
}

const DeliveryDetails: React.FC<React.PropsWithChildren<IDeliveryDetailsProps>> = ({
  serverOrder,
}) => {
  const { delivery, rbiOrderId } = serverOrder;
  const [deliveryStatus, setDeliveryStatus] = useState(delivery!.status);
  const [driver, setDriver] = useState(delivery!.driver || null);
  const [dropoff, setDropoff] = useState(delivery?.dropoff);
  const { formatMessage } = useIntl();
  const eta = useEtaCounter(deliveryStatus, dropoff);
  const { setBringgUuids } = useDeliveryConfirmationContext();
  const { navigate } = useNavigation();

  const { stopPolling } = usePolling({
    queryOptions: fetchDeliveryStatus(rbiOrderId),
    interval: 5000,
    retry: 3,
    onResponse: data => {
      setDropoff(data?.delivery?.dropoff);
      setDeliveryStatus(data?.delivery?.status);
      setDriver(data?.delivery?.driver);
      // Needed to track bringg delivery driver coords
      const share_uuid = data?.delivery?.shareUuid;
      const order_uuid = data?.delivery?.task?.uuid;
      setBringgUuids({ share_uuid, order_uuid });
    },
    onError: () => {
      openErrDialog({
        message: 'There was an error fetching your delivery status. Please try again later.',
      });
    },
  });

  const [ErrorDialog, openErrDialog] = useErrorModal({
    onConfirm: () => navigate(routes.orders),
    modalAppearanceEventMessage: 'Error: Fetching Delivery Details Failure',
  });

  const [CancellationModal, openCancellationModal] = useDialogModal({
    // @ts-expect-error TS(2322) FIXME: Type 'FC<IOrderCancelledModalProps>' is not assign... Remove this comment to see the full error message
    Component: OrderCancelledModal,
    onOpen: stopPolling,
    onDismiss: () => navigate(routes.base),
  });

  useEffect(() => {
    if (deliveryStatus === DeliveryStatus.ORDER_CANCELLED) {
      openCancellationModal();
    }
  }, [deliveryStatus, openCancellationModal]);

  if (!delivery) {
    return null;
  }

  const getDeliveryEtaText = (status: DeliveryStatus): string => {
    switch (status) {
      case DeliveryStatus.DRIVER_AT_CUSTOMER:
        return formatMessage({ id: 'deliveryDriverArrived' });
      case DeliveryStatus.ORDER_DROPPED_OFF:
        return formatMessage({ id: 'deliveryDelivered' });
      default:
        return eta || '...';
    }
  };

  return (
    <>
      <DeliveryCardContainer>
        <CardHeadingContainer focusable>
          <Text>
            {`${formatMessage({ id: 'eta' })}: `}
            <Text testID="order-preparation-confirmation-eta">
              {getDeliveryEtaText(deliveryStatus)}
            </Text>
          </Text>
        </CardHeadingContainer>

        <Divider />

        <CardHeadingContainer focusable>
          <CardHeading testID="order-preparation-confirmation" data-private>
            {`${formatMessage({ id: 'deliveringToYourDoorAt' })}\n`}
            {delivery.dropoff.addressLine1}
          </CardHeading>
        </CardHeadingContainer>

        <DeliveryProgress deliveryStatus={deliveryStatus} />

        {!!driver && (
          <CardHeadingContainer>
            <CourierDetail driver={driver} />
          </CardHeadingContainer>
        )}

        <DeliveryInfo serverOrder={serverOrder} />

        <Divider />

        <DeliveryOrderDetails serverOrder={serverOrder} />
      </DeliveryCardContainer>

      <ErrorDialog />
      <CancellationModal />
    </>
  );
};

export default DeliveryDetails;
